/**
 * Version management API functions.
 */

import apiClient from './client';
import type {
  RequirementVersion,
  VersionCreateRequest,
  PaginatedResponse,
  VersionStatistics,
  PaginatedChangeRecords,
} from '@/types';

/**
 * Get version list for a project.
 */
export const listVersions = async (
  projectId: number,
  params?: {
    page?: number;
    page_size?: number;
    search?: string;
    ordering?: string;
  }
): Promise<PaginatedResponse<RequirementVersion>> => {
  const response = await apiClient.get<PaginatedResponse<RequirementVersion>>(
    `/requirements/${projectId}/versions/`,
    { params }
  );
  return response.data;
};

/**
 * Get version detail (including change records).
 */
export const getVersionDetail = async (
  projectId: number,
  versionId: number,
  params?: {
    page?: number;
    page_size?: number;
    change_type?: string;
  }
): Promise<RequirementVersion> => {
  const response = await apiClient.get<RequirementVersion>(
    `/requirements/${projectId}/versions/${versionId}/`,
    { params }
  );
  return response.data;
};

/**
 * Publish a new version (internal users only).
 */
export const publishVersion = async (
  projectId: number,
  data: VersionCreateRequest
): Promise<RequirementVersion> => {
  const response = await apiClient.post<RequirementVersion>(
    `/requirements/${projectId}/versions/`,
    data
  );
  return response.data;
};

/**
 * Response type for preview changes API.
 */
export interface PreviewChangesResponse {
  has_previous_version: boolean;
  previous_version: {
    id: number;
    version_number: string;
    published_at: string;
  } | null;
  statistics: VersionStatistics;
  change_records: PaginatedChangeRecords;
}

/**
 * Delete the latest version (internal users only).
 */
export const deleteVersion = async (
  projectId: number,
  versionId: number,
): Promise<void> => {
  await apiClient.delete(
    `/requirements/${projectId}/versions/${versionId}/`,
  );
};

/**
 * Preview unpublished changes since last version (read-only, no DB write).
 */
export const previewChanges = async (
  projectId: number,
  params?: {
    page?: number;
    page_size?: number;
    change_type?: string;
  }
): Promise<PreviewChangesResponse> => {
  const response = await apiClient.get<PreviewChangesResponse>(
    `/requirements/${projectId}/preview-changes/`,
    { params }
  );
  return response.data;
};
